"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toRightTriangular = exports.inverseMatrix = exports.determinant = exports.see = exports.allFisherDet = exports.FisherMatrix = exports.negativelogLikelihoodRegularized = exports.hessian = exports.jac = exports.jacobian = exports.iccGenma = void 0;
const tf = require('@tensorflow/tfjs-core');
const tfjs_core_1 = require("@tensorflow/tfjs-core");
/**
 *
 * @param x 1D tensor
 * @returns a diagonal matrix which diagonal elements are the elements of tensor x
 */
const MakeDiag = (x) => {
    return tf.tidy(() => {
        const n = x.shape[0];
        let rows = [];
        for (let i = 0; i < n; i++) {
            const tFront = (0, tfjs_core_1.zeros)([i]);
            const tValue = tf.gather(x, [i]);
            const tBack = tf.zeros([n - i - 1]);
            let fconc = tf.concat([tValue, tBack]);
            rows.push(tf.concat([tFront, fconc]));
        }
        return tf.stack(rows);
    });
};
/**
 *
 * @param x tensor
 * @returns 1/(1+exp(-x))
 */
let sigmoid = (x) => {
    return tf.tidy(() => {
        const res = tf.div(1, tf.add(1, tf.exp(tf.sub(0, x))));
        return res;
    });
};
/**
 * @param theta : level of the student
 * @param a : discrimination of a given item
 * @param b : difficulty of a given item
 * @returns probability that the student answers correctly the item of parameters (a,b) given thetas
 */
let iccGenma = (theta, a, b) => {
    return tf.tidy(() => {
        return sigmoid(tf.add(tf.dot(a, theta), b));
    });
};
exports.iccGenma = iccGenma;
/**
 *
 * @param theta : level of the student
 * @param discriminations : discriminations of all items in the test bank
 * @param difficulties : difficuties of all items in the test bank
 * @param administeredItems : items already asked
 * @param givenAnswers : response vector for the items already asked
 * @param weights : weights given to answers extracted from the BM history
 * @param lambdaReg : regularisation parameter
 * @returns jacobian of the regularized negative log likelihood
 */
let jacobian = (theta, discriminations, difficulties, administeredItems, givenAnswers, weights, lambdaReg = 0.25) => {
    return tf.tidy(() => {
        var administeredDiscriminations = [];
        var administeredDifficulties = [];
        for (let i = 0; i < administeredItems.length; i++) {
            administeredDiscriminations.push(discriminations[administeredItems[i]]);
            administeredDifficulties.push(difficulties[administeredItems[i]]);
        }
        var probabilities = (0, exports.iccGenma)(theta, administeredDiscriminations, administeredDifficulties);
        return tf.sub(tf.dot(tf.transpose(administeredDiscriminations), tf.mul(weights, tf.sub(givenAnswers, probabilities))), tf.mul(2 * lambdaReg, theta)).dataSync();
    });
};
exports.jacobian = jacobian;
let jac = (theta, discriminations, difficulties, administeredItems, givenAnswers, exo_ontology) => {
    return tf.tidy(() => {
        var administeredDiscriminations = [];
        var administeredDifficulties = [];
        for (let i = 0; i < administeredItems.length; i++) {
            administeredDiscriminations.push(discriminations[administeredItems[i]]);
            administeredDifficulties.push(difficulties[administeredItems[i]]);
        }
        var probabilities = (0, exports.iccGenma)(theta, administeredDiscriminations, administeredDifficulties);
        return tf.sub(0, tf.dot(tf.transpose(administeredDiscriminations), tf.sub(givenAnswers, probabilities))).dataSync();
    });
};
exports.jac = jac;
let hessian = (theta, discriminations, difficulties, administeredItems, lambdaReg = 0.25) => {
    return tf.tidy(() => {
        var administeredDiscriminations = [];
        var administeredDifficulties = [];
        for (let i = 0; i < administeredItems.length; i++) {
            administeredDiscriminations.push(discriminations[administeredItems[i]]);
            administeredDifficulties.push(difficulties[administeredItems[i]]);
        }
        var probabilities = (0, exports.iccGenma)(theta, administeredDiscriminations, administeredDifficulties);
        var Wpreproc = tf.mul(probabilities, tf.sub(1, probabilities));
        var W = MakeDiag(Wpreproc);
        var AtWA = tf.matMul(tf.matMul(tf.transpose(administeredDiscriminations), W), administeredDiscriminations);
        return tf.add(AtWA, tf.mul(2 * lambdaReg, tf.eye(theta.length)));
    });
};
exports.hessian = hessian;
/**
 *
 * @param theta : level of the student
 * @param administeredItems : items already asked
 * @param discriminations : discriminations of all items in the bank
 * @param difficulties : difficulties of all items in the bank
 * @param responseVector : stidents answers to the items already asked
 * @param weights : weight given to the answers extracted from the BM history
 * @param lambdaReg : regularisation parameter
 * @returns regularized negative log likelihood
 */
let negativelogLikelihoodRegularized = (theta, administeredItems, discriminations, difficulties, responseVector, weights, lambdaReg = 0.25) => {
    return tf.tidy(() => {
        var administeredDiscriminations = [];
        var administeredDifficulties = [];
        for (let i = 0; i < administeredItems.length; i++) {
            administeredDiscriminations.push(discriminations[administeredItems[i]]);
            administeredDifficulties.push(difficulties[administeredItems[i]]);
        }
        const probabilities = (0, exports.iccGenma)(theta, administeredDiscriminations, administeredDifficulties);
        const tensorWithNan = tf.add(tf.mul(tf.tensor(responseVector), tf.log(probabilities)), tf.mul(tf.sub(1, responseVector), tf.log(tf.sub(1, probabilities))));
        const tensorWithoutNan = tf.where(tf.isNaN(tensorWithNan), tf.zerosLike(tensorWithNan), tensorWithNan);
        const ll = tf.sum(tf.mul(weights, tensorWithoutNan));
        return tf.add(tf.sub(0, ll), tf.mul(lambdaReg, tf.sum(tf.square(theta))));
    });
};
exports.negativelogLikelihoodRegularized = negativelogLikelihoodRegularized;
/**
 *
 * @param discrimination : discrimination vector a of an item
 * @returns dot product <a,a^T>
 */
const discriminationCrossProducts = (discrimination) => {
    return tf.tidy(() => {
        const d = tf.tensor(discrimination, [discrimination.length, 1]);
        return tf.dot(d, tf.transpose(d));
    });
};
/**
 *
 * @param theta : level of the student
 * @param discrimination : discrimination of a given item
 * @param difficulty : difficulty of a given item
 * @returns Fisher Matrix for the given item
 */
let FisherMatrix = (theta, discrimination, difficulty) => {
    return tf.tidy(() => {
        var proba = (0, exports.iccGenma)(theta, discrimination, difficulty).dataSync()[0];
        var crossProducts = discriminationCrossProducts(discrimination);
        return tf.mul(proba * (1 - proba), crossProducts);
    });
};
exports.FisherMatrix = FisherMatrix;
/**
 *
 * @param theta : student level
 * @param discriminations : discriminations of all items in the bank
 * @param difficulties : difficulties of all items in the bank
 * @param currentFisherMat : current Fisher Information Matrix of the test
 * @returns determinant of the sum of the current Fisher Matrix and a given item Fisher Matrix for all items in the bank
 */
let allFisherDet = (theta, discriminations, difficulties, currentFisherMat) => {
    return tf.tidy(() => {
        var allDets = {};
        let count = 0;
        let k = Object.keys(difficulties);
        for (let item of k) {
            var FisherMat = (0, exports.FisherMatrix)(theta, discriminations[item], difficulties[item]);
            var det = (0, exports.determinant)(tf.add(FisherMat, currentFisherMat));
            allDets[item] = det;
        }
        return allDets;
    });
};
exports.allFisherDet = allFisherDet;
/**
 *
 * @param currentFisherMat : current Fisher Matrix of the test
 * @returns estimated variance in each dimension for the theta parameter at the current stage of the test
 */
let variance = (currentFisherMat) => {
    return tf.tidy(() => {
        try {
            const invFisher = inverseMatrix(currentFisherMat).arraySync();
            var diagElements = [];
            for (let i = 0; i < invFisher.length; i++) {
                diagElements.push(invFisher[i][i]);
            }
            return diagElements;
        }
        catch (Error) {
            return Array(currentFisherMat.shape[0]).fill(-Infinity);
        }
    });
};
/**
 *
 * @param currentFisherMat : current Fisher Information Matrix of the test
 * @returns standard error of estimation on theta in each dimension at the current stage of the test
 */
let see = (currentFisherMat) => {
    return tf.tidy(() => {
        try {
            return tf.sqrt(variance(currentFisherMat)).dataSync();
        }
        catch (Error) {
            return Array(currentFisherMat.shape[0]).fill(Infinity);
        }
    });
};
exports.see = see;
/**
 *
 * @param m : matrix (2d tensor)
 * @returns determinant of the matrix computed with Gauss-Jordan Elimination
 */
let determinant = (m) => {
    if (m.shape[0] == 0) {
        return 1;
    }
    var M = (0, exports.toRightTriangular)(m).arraySync();
    var det = M[0][0], n = M.length;
    for (let i = 1; i < n; i++) {
        det = det * M[i][i];
    }
    return det;
};
exports.determinant = determinant;
/**
 *
 * @param m : matrix
 * @param k : given line
 * @param l : given column
 * @returns minor matrix after removing line k and column l
 */
function minorMatrix(m, k, l) {
    var reduced = [];
    const M = m.arraySync();
    for (var i = 0; i < m.shape[0]; i++) {
        if (i == k)
            continue;
        var row = [];
        for (var j = 0; j < m.shape[1]; j++) {
            if (j == l)
                continue;
            row.push(M[i][j]);
        }
        reduced.push(row);
    }
    return tf.tensor(reduced);
}
/**
 *
 * @param m : matrix
 * @param k : given line
 * @param l : given column
 * @returns : determinant of the minor matrix associated to line k and column l
 */
function cofactor(m, k, l) {
    const minor = minorMatrix(m, k, l);
    return (0, exports.determinant)(minor);
}
/**
 *
 * @param m : matrix
 * @returns : comatrix of m
 */
let cofactorMatrix = (m) => {
    var cofactors = [];
    for (var i = 0; i < m.shape[0]; i++) {
        var row = [];
        for (var j = 0; j < m.shape[1]; j++) {
            var cofactorval = cofactor(m, i, j) * Math.pow(-1, i + j);
            row.push(cofactorval);
        }
        cofactors.push(row);
    }
    return tf.tensor(cofactors);
};
/**
 *
 * @param m : matrix
 * @returns inverse matrix of m if it is invertible. Otherwise, an error is thrown
 */
function inverseMatrix(m) {
    return tf.tidy(() => {
        var det = (0, exports.determinant)(m);
        if (det == 0) {
            return tf.tensor([0]);
            // console.log(m.dataSync());
            // throw new Error("SingularMatrix");
        }
        var deti = 1 / det;
        var cof = cofactorMatrix(m);
        var adj = tf.transpose(cof);
        var result = tf.mul(adj, deti);
        return result;
    });
}
exports.inverseMatrix = inverseMatrix;
/**
 *
 * @param m : matrix
 * @returns triangular matrix with the same determinant of m obtained using Gauss-Jordan Elimination
 */
let toRightTriangular = (m) => {
    const M = m.arraySync();
    var n = m.shape[0];
    if (n == 1)
        return tf.tensor([]);
    for (let i = 0; i < n; i++) {
        if (M[i][i] === 0) {
            for (let j = i + 1; j < n; j++) {
                if (M[j][i] !== 0) {
                    var row = [];
                    for (let p = 0; p < n; p++) {
                        row.push(M[i][p] + M[j][p]);
                    }
                    M[i] = row;
                    break;
                }
            }
        }
        if (M[i][i] !== 0) {
            for (let j = i + 1; j < n; j++) {
                var multiplier = M[j][i] / M[i][i];
                row = [];
                for (let p = 0; p < n; p++) {
                    row.push(p <= i ? 0 : M[j][p] - M[i][p] * multiplier);
                }
                M[j] = row;
            }
        }
    }
    return tf.tensor(M);
};
exports.toRightTriangular = toRightTriangular;
